import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AdminDemoData } from '@platform/client';
import { FormsInputDropdown } from '@platform/components';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';

type ApplicationStatusOption =
  | 'not_submitted'
  | 'pending'
  | 'approved'
  | 'approved_match'
  | 'info_requested'
  | 'rejected'
  | 'deactivated';

type NewDemoOrganizationFormData = {
  applicationStatus?: ApplicationStatusOption;
};

export const CreateDemoOrganizationModal = (props: { onClose: () => void }) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const [successText, setSuccessText] = useState<string>();
  const { control, handleSubmit } = useForm<NewDemoOrganizationFormData>({ mode: 'onChange' });

  const createOrgMutator = AdminDemoData.useCreateDemoOrganization({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => setModalStage('error'),
      onSuccess: (data) => {
        const applicationNote = data.applicationStatus ? `, application ${data.applicationStatus}` : '';
        setSuccessText(
          `Organization #${data.organizationId} (${data.organizationName}) created, with representative #${data.representativeId} (${data.representativeName})${applicationNote}.`,
        );
        setModalStage('success');
      },
    },
  });

  const onSubmit: SubmitHandler<NewDemoOrganizationFormData> = (data) => {
    createOrgMutator.mutate({
      data: {
        applicationStatus:
          data.applicationStatus && data.applicationStatus !== 'not_submitted' ?
            data.applicationStatus
          : null,
      },
    });
  };

  return (
    <AdminFormModal
      title='Create Demo Organization'
      onClose={props.onClose}
      modalStage={modalStage}
      successText={successText}
      submitCTA={{
        text: 'Create',
        callback: () => {
          handleSubmit(onSubmit)();
        },
      }}
    >
      <div className='space-y-2'>
        <p className='text-sm text-gray-600'>
          Creates a new organization with realistic placeholder details and one attached
          representative user. Select an application status below to make it ready for a demo
          project, leave it unset to demo the org sign-up flow instead.
        </p>
        <FormsInputDropdown
          type='default'
          control={control}
          name='applicationStatus'
          label='Application Status'
          placeholder='Not submitted (show sign-up flow)'
          items={[
            { text: 'Not submitted (show sign-up flow)', textValue: 'not_submitted' },
            { text: 'Pending review', textValue: 'pending' },
            { text: 'Approved (full platform access)', textValue: 'approved' },
            { text: 'Approved — limited access', textValue: 'approved_match' },
            { text: 'Info requested', textValue: 'info_requested' },
            { text: 'Rejected', textValue: 'rejected' },
            { text: 'Deactivated', textValue: 'deactivated' },
          ]}
          error={undefined}
        />
      </div>
    </AdminFormModal>
  );
};
